import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserPlus, Pencil, Trash2, Download } from 'lucide-react';
import { exportsApi, saveBlob, ExportError } from '../../../../api/exports';
import { useAuth } from '../../../../contexts/AuthContext';
import { usersApi } from '../../../../api/users';
import { jobTitlesApi } from '../../../../api/job-titles';
import { User, Permission, PermissionEnum, JobTitle } from '../../../../types';
import { formatDate } from '../../../../lib/utils';
import { useDebouncedValue } from '../../../../lib/useDebouncedValue';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { Badge } from '../../../../components/ui/atoms/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, SortableTableHead } from '../../../../components/ui/molecules/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../components/ui/organisms/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '../../../../components/ui/organisms/form';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { SearchableSelect } from '../../../../components/ui/molecules/searchable-select';
import { PermissionCheckboxes } from '../components';
import { getErrorMessage } from '../utils';

// ── Schemas ───────────────────────────────────────────────────────────────────

const createUserSchema = z.object({
  name: z.string().min(6, 'Name must be at least 6 characters'),
  email: z.string().email('Invalid email'),
  managerId: z.string().min(1, 'Manager is required'),
  jobTitleId: z.string().min(1, 'Job title is required'),
  permissions: z.array(z.string()).min(1, 'At least one permission required'),
});
type CreateUserValues = z.infer<typeof createUserSchema>;

const editUserSchema = z.object({
  name: z.string().min(6, 'Name must be at least 6 characters'),
  managerId: z.string().optional(),
  jobTitleId: z.string().min(1, 'Job title is required'),
  permissions: z.array(z.string()).min(1, 'At least one permission required'),
});
type EditUserValues = z.infer<typeof editUserSchema>;

export function UsersTab() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [jobTitleFilter, setJobTitleFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const { hasPermission } = useAuth();
  const canExport = hasPermission(PermissionEnum.EXPORT_DATA);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'manager' | 'createdAt'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  function toggleSort(column: 'name' | 'manager' | 'createdAt') {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  }

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, jobTitleFilter, sortBy, sortDir]);

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, debouncedSearch, jobTitleFilter, sortBy, sortDir],
    queryFn: () =>
      usersApi
        .list({
          page,
          limit: 20,
          name: debouncedSearch || undefined,
          jobTitleId: jobTitleFilter || undefined,
          includeDeleted: true,
          sortBy,
          sortDir,
        })
        .then((r) => r.data),
  });

  const { data: allUsersData } = useQuery({
    queryKey: ['users-all'],
    queryFn: () => usersApi.list({ limit: 200 }).then((r) => r.data),
    enabled: createOpen || !!editUser,
  });

  const { data: jobTitlesData } = useQuery({
    queryKey: ['job-titles'],
    queryFn: () => jobTitlesApi.list().then((r) => r.data),
  });

  // Active users first, deleted users last — stable within each group.
  const users: User[] = [...(data?.data ?? [])].sort((a, b) => {
    const aDeleted = a.deletedAt ? 1 : 0;
    const bDeleted = b.deletedAt ? 1 : 0;
    return aDeleted - bDeleted;
  });
  const managers: User[] = allUsersData?.data ?? [];
  const jobTitlesList: JobTitle[] = jobTitlesData ?? [];
  const meta = data?.meta;

  // Create form
  const createForm = useForm<CreateUserValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { name: '', email: '', managerId: '', jobTitleId: '', permissions: [PermissionEnum.LOG_HOURS] },
  });

  const createMutation = useMutation({
    mutationFn: (v: CreateUserValues) => usersApi.create(v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['job-titles'] });
      createForm.reset();
      setCreateOpen(false);
    },
  });

  // Edit form
  const editForm = useForm<EditUserValues>({
    resolver: zodResolver(editUserSchema),
    defaultValues: { name: '', managerId: '', jobTitleId: '', permissions: [PermissionEnum.LOG_HOURS] },
  });

  const editMutation = useMutation({
    mutationFn: (v: EditUserValues) => {
      const { managerId, ...rest } = v;
      const payload = managerId && managerId.trim()
        ? { ...rest, managerId }
        : rest;
      return usersApi.update(editUser!.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['job-titles'] });
      setEditUser(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['job-titles'] });
      setDeleteUserId(null);
    },
  });

  // The backend builds the file from the same filters, sort and search as this table.
  async function exportView() {
    setExportMessage('');
    setIsExporting(true);
    try {
      const file = await exportsApi.users({ name: debouncedSearch, jobTitleId: jobTitleFilter, includeDeleted: true, sortBy, sortDir });
      saveBlob(file.blob, file.filename);
    } catch (error) {
      // Includes the "no users match" case: the backend's message explains it.
      setExportMessage(error instanceof ExportError ? error.message : 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }

  function openEdit(user: User) {
    editForm.reset({
      name: user.name,
      managerId: user.managerId ?? '',
      jobTitleId: user.jobTitleId ?? '',
      permissions: user.permissions,
    });
    setEditUser(user);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-56"
          />
          <SearchableSelect
            value={jobTitleFilter}
            onValueChange={setJobTitleFilter}
            options={jobTitlesList.map((jt) => ({ value: jt.id, label: jt.name }))}
            allowEmpty
            emptyLabel="All job titles"
            placeholder="All job titles"
            searchPlaceholder="Search job titles..."
            className="w-48"
          />
        </div>
        <div className="flex items-center gap-2">
        {exportMessage && <span className="text-xs text-muted-foreground">{exportMessage}</span>}
        {canExport && (
          <Button variant="outline" onClick={exportView} disabled={isExporting || isLoading}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting…' : 'Export'}
          </Button>
        )}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create User</DialogTitle>
            </DialogHeader>
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit((v) => createMutation.mutate(v))} className="space-y-4">
                <FormField
                  control={createForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl><Input placeholder="Jane Doe" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl><Input type="email" placeholder="jane@finaira.com" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="managerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Manager</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {managers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="jobTitleId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Title</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Select job title" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {jobTitlesList.map((jt) => (
                            <SelectItem key={jt.id} value={jt.id}>{jt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="permissions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Permissions</FormLabel>
                      <FormControl>
                        <PermissionCheckboxes value={field.value} onChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {createMutation.error && (
                  <p className="text-sm text-destructive">
                    {getErrorMessage(createMutation.error, 'Failed to create user')}
                  </p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? 'Creating…' : 'Create User'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit((v) => editMutation.mutate(v))} className="space-y-4">
              <FormField
                control={editForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="managerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Manager</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {managers.filter((u) => u.id !== editUser?.id).map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="jobTitleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Select job title" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {jobTitlesList.map((jt) => (
                          <SelectItem key={jt.id} value={jt.id}>{jt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="permissions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Permissions</FormLabel>
                    <FormControl>
                      <PermissionCheckboxes value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {editMutation.error && (
                <p className="text-sm text-destructive">
                  {getErrorMessage(editMutation.error, 'Failed to update user')}
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
                <Button type="submit" disabled={editMutation.isPending}>
                  {editMutation.isPending ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteUserId} onOpenChange={(o) => { if (!o) setDeleteUserId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This user will be marked as deleted and hidden from active lists, but their historical
            records (time entries, audit logs) will be preserved.
          </p>
          {deleteMutation.error && (
            <p className="text-sm text-destructive">
              {getErrorMessage(deleteMutation.error, 'Failed to delete user')}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDeleteUserId(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteUserId && deleteMutation.mutate(deleteUserId)}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead sorted={sortBy === 'name' && sortDir} onSort={() => toggleSort('name')}>Name</SortableTableHead>
                <TableHead>Email</TableHead>
                <TableHead>Job Title</TableHead>
                <SortableTableHead sorted={sortBy === 'manager' && sortDir} onSort={() => toggleSort('manager')}>Manager</SortableTableHead>
                <TableHead>Permissions</TableHead>
                <SortableTableHead sorted={sortBy === 'createdAt' && sortDir} onSort={() => toggleSort('createdAt')}>Joined</SortableTableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">No users found.</TableCell>
                </TableRow>
              )}
              {users.map((user) => {
                const isDeleted = !!user.deletedAt;
                return (
                  <TableRow key={user.id} className={isDeleted ? 'opacity-50' : undefined}>
                    <TableCell className="font-medium">
                      {user.name}
                      {isDeleted && (
                        <Badge variant="destructive" className="ml-2 text-xs">Deleted</Badge>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell className="text-muted-foreground">{user.jobTitle?.name ?? '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{user.manager?.name ?? '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.permissions.map((p: Permission) => (
                          <Badge key={p} variant="secondary" className="text-xs">{p.replace(/_/g, ' ')}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      {!isDeleted && (
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(user)} title="Edit">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setDeleteUserId(user.id)} title="Delete">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">Page {meta.page} of {meta.totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}